<template>
    <div class="draggable rounded" :class="getBGClass()" v-if="(section.status!=='OMITTED') || showOmitted ">
        <div class="flex items-center mb-1">
            <!-- Draggable handle. -->
            <div class="w-6 flex-shrink-0">
                <i class="fa fa-bars text-lg text-gray-400 hover:text-gray-500 cursor-move handle" v-if="canMove()"></i>
            </div>
            <div class="flex-grow">
                <div
                    v-if="!canEdit()"
                    class="whitespace-pre-line text-lg m-0 px-2 py-1 font-bold text-gray-900 border-transparent rounded shadow-none relative focus:z-10 resize-none"
                    :class="((hasChanged() || (status==='OMITTED') || (proposedStatus==='OMITTED')) ?' text-red-600':'') + (((status==='OMITTED') || proposedStatus==='OMITTED')?' line-through':'') + getBGClass()"
                    v-text="name"
                ></div>
                <input
                    :placeholder="'Section name'"
                    class="text-lg m-0 px-2 py-1 font-bold text-gray-900 border-transparent rounded shadow-none relative focus:z-10"
                    :class="((hasChanged() || (status==='OMITTED') || (proposedStatus==='OMITTED')) ?' text-red-600':'') + (((status==='OMITTED') || proposedStatus==='OMITTED')?' line-through':'') + getBGClass()"
                    ref="sectionName"
                    type="text"
                    @input="update()"
                    @change="addSnapshot"
                    v-else
                    v-model="name"
                >
            </div>
            <div class="w-6 flex-shrink-0 ml-2 self-start py-1.5">
                <ui-dropdown menu-class="p-3 w-48" v-if="canEdit()">
                    <template #default="{ toggle }">
                        <button @click="toggle"
                                class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                            <i class="fa fa-ellipsis-h fa-fw"></i>
                        </button>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-trash" v-if="proposedStatus==='ADDED'" danger
                                          @click="omitSection(true); update(); close()">
                            Remove
                            addition
                        </ui-dropdown-item>
                        <ui-dropdown-item icon="fas fa-trash" v-else-if="proposedStatus!=='OMITTED'" danger
                                          @click="omitSection(true); update(); close()">Omit section
                        </ui-dropdown-item>
                        <ui-dropdown-item icon="fas fa-trash" v-else @click="omitSection(false); update(); close()">
                            Revert
                            omission
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="hasChanged()"></ui-dropdown-divider>
                        <ui-dropdown-item icon="fas fa-undo" v-if="hasChanged()"
                                          @click="revert(); update(); close()">
                            Revert
                            changes
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>
            </div>

        </div>
        <draggable :list="section.tasks" draggable=".draggable" handle=".handle"
                   :group="'tasks'+(((proposedStatus==='OMITTED') || (status==='OMITTED'))?'Off':'')" @change="update">
            <contracted-works-task v-for="task in section.tasks" :key="task.id" :showItemProgress="showItemProgress"
                                   :search="search"
                                   :readonly="readonly"
                                   :contracted-works="contractedWorks"
                                   :show-omitted="showOmitted"
                                   :section="section"
                                   :latest="latest"
                                   @updated="update"
                                   :currently-building="currentlyBuilding"
                                   :current-viewing="currentViewing"
                                   @editBuildup="(i) => $emit('editBuildup',i)"
                                   :show-total="showTotal"
                                   :latest-progress-report="latestProgressReport"
                                   :task="task" :ref="(readonly?'readonly-':'')+'tasks'"></contracted-works-task>
        </draggable>

        <!-- New Task Item. -->
        <div class="flex -mb-px" v-if="canEdit() && (status!=='OMITTED') && (proposedStatus!=='OMITTED')"
             :class="getBGClass()">
            <div class="w-6 flex-shrink-0 self-center">
                <i class="fa fa-plus text-xs font-bold text-gray-300"></i>
            </div>
            <div class="w-16">
                <input
                    :placeholder="'Ref...'"
                    :class="getBGClass()"
                    class="text-sm m-0 px-2 py-1 font-bold text-gray-900 border-transparent rounded shadow-none relative focus:z-10"
                    type="text"
                    @input="addNewFromReference"
                    v-model="newTask.reference"
                >
            </div>
            <div class="flex-1 ml-0">
                <input
                    placeholder="Add new task..."
                    class="text-sm m-0 px-2 py-1 font-bold text-gray-900 border-transparent rounded shadow-none relative focus:z-10"
                    :class="getBGClass()"
                    type="text"
                    ref="name"
                    @input="addNewFromName"
                    v-model="newTask.name"
                >
            </div>
            <div class="w-6 flex-shrink-0 ml-2 self-start py-1.5">
            </div>
        </div>

        <!-- Total Amount. -->
        <div class="flex text-xs text-gray-500 text-right font-semibold bg-white" v-if="showTotal" :key="totalValueKey">
            <div class="flex-1 px-2 py-1">Section total</div>
            <div class="w-20 -ml-px px-2 py-1" v-text="totalValue()">
            </div>
            <div class="w-6 flex-shrink-0 ml-2"></div>
        </div>
        <div class="py-2" v-else></div>

    </div>
</template>

<script>
import draggable from 'vuedraggable'
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import {toCurrency, uuid} from "../../../utils";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import ContractedWorksTask from "./ContractedWorksTask.vue";
import ContractedWorksTimelinePanel from "./ContractedWorksTimelinePanel.vue";

export default {
    components: {
        ContractedWorksTimelinePanel,
        ContractedWorksTask,
        UiDropdownDivider,
        UiDropdown,
        UiDropdownItem,
        draggable,
    },
    props: {
        search: '',
        latest: Object,
        contractedWorks: Object,
        contract: Object,
        section: Object,
        showItemProgress: Boolean,
        showOmitted: Boolean,
        readonly: true,
        showTotal: false,
        currentViewing: false,
        currentlyBuilding: false,
        latestProgressReport: Object,
    },
    data() {
        return {
            newTask: {
                reference: null,
                name: null,
            },
            name: this.section.name,
            proposedStatus: this.section.proposedStatus,
            status: this.section.status,
            original: this.latest ? this.latest.scopeOfWorks.find((i) => i.id === this.section.id) : null,
            totalValueKey: 0,
        }
    },
    computed: {},
    methods: {
        totalValue() {
            let value = 0;
            this.section.tasks.forEach((task) => {
                task.items.forEach((i) => {
                    if ((i.status === 'OMITTED') || (i.proposedStatus === 'OMITTED')) {
                    } else {
                        value += parseFloat(i.value ? (i.value.replace(/[^\d.-]/g, '')) : 0);
                    }
                })
            })
            return toCurrency(value)
        },
        canEdit() {
            return (!this.readonly)
        },
        canMove() {
            return (!this.readonly) && (this.proposedStatus === 'ADDED');
        },
        omitSection(omitted) {
            if (this.proposedStatus === 'ADDED') {
                this.contract.scopeOfWorks.splice(this.contract.scopeOfWorks.indexOf(this.contract.scopeOfWorks.find((i) => i.id === this.section.id)), 1)
                this.$emit('deleted')
            } else {
                this.revert()
                if (omitted) {
                    this.proposedStatus = 'OMITTED'
                } else {
                    this.proposedStatus = null;
                }
                if (this.$refs.tasks) {
                    this.$refs.tasks.forEach((i) => {
                        i.omitTask(omitted)
                    })
                }
            }
            this.updateState();
        },
        addNewFromReference() {
            this.section.tasks.push({
                id: uuid(),
                reference: this.newTask.reference,
                name: this.newTask.name,
                items: [],
                proposedStatus: 'ADDED',
            })
            this.$nextTick(() => {
                this.$refs.tasks.at(-1).update()
                this.newTask = {reference: null, name: null}
                this.$refs.tasks.at(-1).$refs.reference.focus()
            })
        },
        addNewFromName() {
            this.section.tasks.push({
                id: uuid(),
                reference: this.newTask.reference,
                name: this.newTask.name,
                items: [],
                proposedStatus: 'ADDED',
            })
            this.$nextTick(() => {
                this.$refs.tasks.at(-1).update()
                this.newTask = {reference: null, name: null}
                this.$refs.tasks.at(-1).$refs.name.focus()
            })
        },
        updateState() {
            this.section.name = this.name
            this.section.proposedStatus = this.proposedStatus
            this.totalValueKey++;
        },
        update(snapshot) {
            this.updateState()
            this.$emit('updated', snapshot)
        },
        addSnapshot() {
            this.updateState()
            this.$emit('updated', true)
        },
        revert() {
            if (this.hasChanged()) {
                this.section.name = this.original.name
                this.name = this.section.name
            }
            this.proposedStatus = null
        },
        hasChanged() {
            return (this.original && (this.original.name !== this.name))
        },
        getBGClass() {
            if (this.proposedStatus === 'OMITTED') {
                return ' bg-red-50';
            }
            if (this.proposedStatus === 'ADDED') {
                return ' bg-green-50';
            }
            if (this.hasChanged() || (this.proposedStatus === 'SUBSTITUTED')) {
                return ' bg-orange-50'
            }
            return ' bg-white';
        }
    },
}
</script>
